/* eslint-disable no-param-reassign */
import {
  memo,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash.clonedeep';
import { useHistory } from 'react-router-dom';
import { FullScreen } from 'react-full-screen';
// components
// import { useReactToPrint } from 'react-to-print';
import GridKpisCard, { ReportGridCard } from '../../juristec-ui/kpis/grid/CardContainer';
import InfoKpi from '../Modals/InfoKpi';
import ModifierByValue from '../Modals/ModifierByValue';
import KpiViewer from '../KpiViewer';
import Button from '../../juristec-ui/core/Button';

// contexts and others
// import { AlertContext, ModalContext } from '../Juristec/core';
import { AlertContext } from '../../context/AlertProvider';
import { ModalContext } from '../../context/ModalProvider';
import urlHash from '../../juristec-ui/utils/functions/urlHash';
import { formatDateTime } from '../../juristec-ui/utils/functions/lab';
import useDeviceType from '../../juristec-ui/hooks/useDeviceType';
import TransferKpi, { ResponseList } from '../Modals/TransferKpi';

// styles
import {
  StyledResponsiveReactGridKpiLayout,
  StyledOuterGridKpiLayout,
  ExitFullScreen,
  CardWrapper,
  EmptyReportCard,
} from './styled/GridKpis.styled';

import getCardStyle from '../../utils/functions/getCardStyle';

import '../../juristec-ui/styles/react-grid-layout.css';
import '../../juristec-ui/styles/react-resizable.css';

// ReactGridLayout variables
const rglCols = {
  lg: 48,
  // md: 20,
  sm: 12,
  // xs: 8,
  xxs: 4,
};

/* const breakpoints = {
  lg: 1200,
  md: 1024,
  sm: 768,
  xs: 480,
  xxs: 0,
}; */

const breakpoints = {
  lg: 960,
  sm: 600,
  xxs: 0,
};

const setBreakPoints = (layoutType) => {
  switch (layoutType) {
    case 'desktop':
      return 'lg';
    case 'tablet':
      return 'sm';
    case 'mobile':
      return 'xxs';
    default:
      return null;
  }
};

const itemTypeLabel = (itemType) => {
  switch (itemType) {
    case 'label':
    case 'smartLabel':
    case 'Content':
      return 'Insight';
    case 'Iframe':
      return 'Conteúdo';
    default:
      return 'KPI';
  }
};

// funcao para redimensionar o card de acordo com a altura do conteudo, para que nao fique com barra de rolagem
const AutoHeightReport = ({ children, onHeightChange }) => {
  const contentRef = useRef(null);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return undefined;

    let animationFrame;

    const measure = () => {
      cancelAnimationFrame(animationFrame);

      animationFrame = requestAnimationFrame(() => {
        // Não usar a altura da tela inteira como altura do card.
        if (document.fullscreenElement) return;

        onHeightChange(element.getBoundingClientRect().height);
      });
    };

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    document.addEventListener('fullscreenchange', measure);
    measure();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrame);
      document.removeEventListener('fullscreenchange', measure);
    };
  }, [onHeightChange]);

  return (
    <div ref={contentRef} style={{ display: 'flow-root' }}>
      {children}
    </div>
  );
};

const GridKpis = memo(({
  gridKpiRef = null,
  isOwner = false,
  isSuper = false,
  isGuest = true,
  gridKpiAPI = {
    cloneCard: () => {},
    transferKpi: () => {},
    removeCard: () => {},
    downloadImgCard: () => {},
    updateKpiShowOnExpand: () => {},
    downloadReportFromCard: () => {},
    saveKpiComment: () => {},
    loadCardsFromCollection: () => {},
  },
  gridKpiState = {},
  dashboards = [],
  editPainel = false,
  multiSelection = false,
  selectedCards,
  toggleSelected,
  gridSize = 'auto',
  saveLayout = () => {},
  dashboardKey,
  kpiItemList = [],
  dashboardDoc = {},
  dashboardFileDependencies,
  kpisLayout = {},
  gridBreakpoint = '',
  setGridBreakpoint = () => {},
  showToolbar = true,
  showToPrint = true,
  //
  getControlValues = () => {},
  handleDynamicKpi = () => {},
  fullScreenHandler,
  checkAccessToDependency = () => {},
  checkUserPermission = () => {},
  getKpiWithoutDependencyMessage = () => {},
  handleIframe = () => {},
  handleSubsetTable = () => {},
  handleValueModifiers = () => {},
  //
  globalFilters,
  globalFiltersOrder,
  getUserName = () => {},
  subsetTableReachedLimit = () => {},
  subsetTableLimit = 1,
  plan = 'advanced',
  //
  reportDefinitions = {},
  onReportRetry = () => {},
  onReportDensityChange = () => {},
  handleReportGridSettings = () => {},
  handleReportGridDetails = () => {},

}) => {
  const history = useHistory();

  const { setAlertConfig } = useContext(AlertContext);
  const { setModalConfig, closeModal } = useContext(ModalContext);
  const device = useDeviceType();

  const [stateKpisLayout, setStateKpisLayout] = useState({
    lg: [],
    // md: [],
    sm: [],
    // xs: [],
    xxs: [],
  });

  // const [tempKpis, setTempKpis] = useState(new Map());
  const [viewKpiId, setViewKpiId] = useState(null);

  useEffect(() => {
    if (kpisLayout) {
      setStateKpisLayout({
        lg: kpisLayout.lg,
        sm: kpisLayout.sm,
        xxs: kpisLayout.xxs,
      });
    }
  }, [kpisLayout]);

  // valor começa com null no index.
  useEffect(() => {
    if (editPainel === false) saveLayout(stateKpisLayout);
  }, [editPainel]);

  const getSpaceBetween = (obj) => {
    if (obj) return [+obj.spaceBetween || 0, +obj.spaceBetween || 0];

    return [10, 10];
  };

  const [reportHeights, setReportHeights] = useState({});

  const verticalMargin = getSpaceBetween(dashboardDoc?.cardStyles)[1];

  const updateReportHeight = useCallback((id, height) => {
    if (!gridBreakpoint || height <= 0) return;

    const h = Math.max(
      1,
      Math.ceil((height + verticalMargin) / (20 + verticalMargin)),
    );

    setReportHeights((previous) => {
      if (previous[gridBreakpoint]?.[id] === h) return previous;

      return {
        ...previous,
        [gridBreakpoint]: {
          ...previous[gridBreakpoint],
          [id]: h,
        },
      };
    });
  }, [verticalMargin]);

  const layoutsWithReportHeights = useMemo(() => {
    const reportIds = new Set(
      kpiItemList
        .filter((item) => item.type === 'ReportGrid')
        .map((item) => item.id),
    );

    return Object.fromEntries(
      ['lg', 'sm', 'xxs'].map((breakpoint) => {
        const layout = Array.isArray(kpisLayout?.[breakpoint])
          ? kpisLayout[breakpoint]
          : [];

        return [
          breakpoint,
          layout.map((entry) => {
            if (!reportIds.has(entry.i)) return entry;

            const measuredHeight = reportHeights[breakpoint]?.[entry.i];

            return {
              ...entry,
              ...(measuredHeight !== undefined
                ? { h: measuredHeight }
                : {}),
              isResizable: !!editPainel,
              resizeHandles: ['e'],
            };
          }),
        ];
      }),
    );
  }, [kpisLayout, kpiItemList, reportHeights, editPainel]);

  // ///////////// handlers ////////////// //
  const openCardInfo = (kpiInfos) => {
    const formatOldGroups = (gObjOld) => {
      if (!gObjOld.rules) return [];
      return (
        Object.keys(gObjOld.rules).reduce((aux, rName) => {
          aux.push({
            column: gObjOld.target,
            ftype: 'values',
            rule: rName,
            selector: '',
            inverse: false,
            values: gObjOld.rules[rName],
          });
          return aux;
        }, [])
      );
    };
    const fileOrigin = dashboardFileDependencies.find((file) => (
      file.file_id === kpiInfos?.database
    ));

    const getGroups = () => {
      const fGroups = kpiInfos?.meta?.groupValues || [];
      const groups = ['columnsMap', 'linesMap', 'drilldownMap', 'maps'].reduce((aux, k) => {
        const gp = cloneDeep(kpiInfos?.meta[k]);
        if (gp) {
          if (!Array.isArray(gp)) aux[k] = formatOldGroups(gp);
          else aux[k] = gp;
        }
        return aux;
      }, {});

      if (fGroups.length > 0) {
        fGroups.forEach((g) => {
          const key = kpiInfos.meta?.columns?.[0]?.column === g.column ? 'columnsMap' : 'linesMap';
          if (groups[key]) groups[key].push({ ...g, isValueGroup: true });
          else groups[key] = [{ ...g, isValueGroup: true }];
        });
      }
      return groups;
    };

    const getFilters = () => {
      const bFilters = kpiInfos?.meta?.filters || [];
      const fFilters = kpiInfos?.meta?.filterValues?.map((f) => {
        f.isValueFilter = true; return f;
      }) || [];
      return bFilters.concat(fFilters);
    };

    const owner = getUserName(kpiInfos?.meta?.ownerId);

    if (fileOrigin) {
      setModalConfig({
        title: 'Informações do KPI',
        yesLabel: 'Ok',
        children: (
          <InfoKpi
            creationDate={formatDateTime(kpiInfos.createdAt, { time: 'none' })}
            lastUpdateDate={formatDateTime(kpiInfos.updatedAt, { time: 'none' })}
            kpiName={kpiInfos.name || ''}
            fileName={fileOrigin.filename || ''}
            dataValues={kpiInfos.meta?.values}
            dataLine={kpiInfos.meta?.lines?.[0]}
            dataColumn={kpiInfos.meta?.columns?.[0]}
            dataControl={kpiInfos.meta?.control?.[0]}
            dataDrilldown={kpiInfos.meta?.drilldown?.[0]}
            chartType={kpiInfos?.type}
            filters={getFilters()}
            groupings={getGroups()}
            device={device}
            owner={owner}
          />
        ),
      });
    } else {
      setAlertConfig({
        type: 'error',
        text: 'Erro ao tentar obter as informações deste KPI',
        child: 'O arquivo responsável pela geração deste KPI foi excluído. Desta forma, não é possível verificar as informações relativas a este KPI!',
      });
    }
  };

  const deleteCard = async (card) => {
    const verifyDelete = async () => {
      const result = await gridKpiAPI.removeCards(
        [card], kpiItemList, kpisLayout,
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
        });
        return;
      }

      setAlertConfig({
        type: 'success',
        text: `${itemTypeLabel(card.type)} removido com sucesso!`,
      });
    };

    setAlertConfig({
      type: 'warning',
      text: `Tem certeza que deseja remover o ${itemTypeLabel(card.type)}${card.name ? ` ${card.name}` : ''}?`,
      child: 'Este kpi não poderá ser restaurado após a remoção.',
      withoutConfirm: true,
      withFunction: true,
      confirmFunction: verifyDelete,
    });
  };

  const downloadReport = async (kpiId, reportName, tempFilters) => {
    const result = await gridKpiAPI.downloadReportFromCard(
      kpiId, reportName, tempFilters,
    );
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
    }
  };

  const editCard = async (item) => {
    switch (item.type) {
      case 'Iframe':
        return handleIframe(item);
      case 'SubsetTable':
        return handleSubsetTable(item);
      case 'label':
      case 'smartLabel':
      case 'Content':
        return history.push(`/label/${urlHash(`${dashboardKey}/${item.id}`)}`);
      default:
        return history.push(`/kpi/${urlHash(`${dashboardKey}/${item.id}`)}`);
    }
    // if (item.type === 'Iframe') {
    //   return handleIframe(item);
    // }
    // const hash = urlHash(`${dashboardKey}/${item.id}`);
    // if (['label', 'smartLabel'].includes(item.type)) return history.push(`/label/${hash}`);

    // return history.push(`/kpi/${hash}`);
  };

  const cloneCard = async (item) => {
    if (item.type === 'SubsetTable' && subsetTableReachedLimit()) {
      setAlertConfig({
        type: 'warning',
        text: 'Limite de Subtabelas atingido!',
        child: `Seu plano permite ${subsetTableLimit} Subtabela${subsetTableLimit > 1 ? 's' : ''} por Dashboard. Para alterar o plano, entre em contato com o suporte.`,
      });
      return;
    }
    const result = await gridKpiAPI.cloneCard(item, kpisLayout);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
      return;
    }
    setAlertConfig({
      type: 'success',
      text: `${itemTypeLabel(item.type)} clonado com sucesso`,
    });
  };

  const transferKpi = async (item) => {
    const handleSubmit = async (targetDashs) => {
      const result = await gridKpiAPI.transferKpis(
        [item],
        targetDashs,
        kpisLayout,
        subsetTableLimit,
        plan,
      );
      if (result.error) {
        setAlertConfig({
          type: 'error',
          text: result.msg,
          child: `${result.raw}`,
          okFunction: closeModal,
        });
        return;
      }
      const s = (prefix = '') => (selectedCards.size > 1 ? `${prefix}s` : '');
      let status = 'success';
      let mainMessage = `Transferência da${s()} cópia${s()} do${s()} KPI${s("'")} realizada com sucesso!`;
      if ((result.errors.length + result.fileErrors.length) > 0) {
        if (result.updated.length > 0) {
          status = 'warning';
          mainMessage = `Transferência da${s()} cópia${s()} do${s()} KPI${s("'")} realizada parcialmente!`;
        } else {
          status = 'error';
          mainMessage = `Não foi possível realizar a transferência da${s()} cópia${s()} do${s()} KPI${s("'")}`;
        }
      }
      setAlertConfig({
        type: status,
        text: mainMessage,
        child: (
          <ResponseList
            updatedDashs={result.updated}
            errorDashs={result.errors}
            errorFiles={result.fileErrors}
            dashboards={dashboards}
            fileList={dashboardFileDependencies}
          />
        ),
        okFunction: closeModal,
      });
    };

    const options = dashboards.map((dashboard) => (
      {
        ...dashboard,
        label: dashboard.name,
        value: dashboard.id,
      }
    ));

    setModalConfig({
      title: 'Transferir Cópia',
      children: (
        <TransferKpi
          currentKpis={[item]}
          dashOptions={options}
          close={closeModal}
          submit={handleSubmit}
        />
      ),
    });
  };
  const updateKpiShowOnExpand = async (itemId, showOnExpand) => {
    const result = await gridKpiAPI.updateKpiShowOnExpand(itemId, showOnExpand);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
    } else {
      await gridKpiAPI.loadCardsFromCollection();
    }
  };
  // Function to save edit the KPI document adding the comment
  const addComment = async (msg, itemId) => {
    const result = await gridKpiAPI.saveKpiComment(itemId, msg);
    if (result.error) {
      setAlertConfig({
        type: 'error',
        text: result.msg,
        child: `${result.raw}`,
      });
    } else {
      await gridKpiAPI.loadCardsFromCollection();
    }
  };

  const submitValueModifiers = async (kpiInfos, filtersList, groupList) => {
    const metaGen = {
      ...kpiInfos.meta,
      filterValues: filtersList,
      groupValues: groupList,
    };
    const res = await handleValueModifiers(kpiInfos?.database, kpiInfos.id, metaGen);
    if (res) {
      await gridKpiAPI.loadCardsFromCollection();
    }
    closeModal();
  };

  const openModifiersModal = (kpiInfos) => {
    const handleSubmitValueModifiers = async (filtersList, groupList) => {
      submitValueModifiers(kpiInfos, filtersList, groupList);
    };

    setModalConfig({
      title: 'Filtrar e Agrupar',
      minWidth: '400px',
      children: (
        <ModifierByValue
          lineName={kpiInfos?.meta?.lines?.[0]?.column}
          columnName={kpiInfos?.meta?.columns?.[0]?.column}
          filtersList={kpiInfos.meta.filterValues}
          groupList={kpiInfos.meta.groupValues}
          closeModal={closeModal}
          submitData={handleSubmitValueModifiers}
        />
      ),
    });
  };

  const holdBreakpoint = (bk) => {
    if (!editPainel) {
      setGridBreakpoint(bk);
    }
  };

  const getLayoutOrder = useMemo(() => {
    const tempL = [
      ...kpisLayout[gridBreakpoint],
    ].filter(
      (l) => !!kpiItemList.find(
        (k) => k.id === l.i
      && (k.showOnExpand ?? true
      ),
      ),
    );
    const qtdCols = rglCols[gridBreakpoint];
    return tempL.sort((a, b) => (a.y * qtdCols + a.x >= b.y * qtdCols + b.x ? 1 : -1));
  }, [gridBreakpoint, kpiItemList]);

  const getKpiSize = (kpiId) => {
    const nodeKpi = document.getElementById(kpiId);
    return { h: nodeKpi?.clientHeight || 0, w: nodeKpi?.clientWidth || 0 };
  };

  const openKpiViewer = (
    id,
    kpiItem,
    pageState,
    kpiLoading,
    drilldownKeys,
    handleDrildown,
    removeDrilldown,
    autoValueModifiers,
  ) => {
    setModalConfig({
      title: '',
      transparent: true,
      children: (
        <KpiViewer
          show
          close={closeModal}
          kpiItem={kpiItem}
          layout={getLayoutOrder}
          getKpiSize={getKpiSize}
          cardStyles={dashboardDoc?.cardStyles}
          collection={gridKpiState?.collection}
          changeKpi={setViewKpiId}
          pageState={pageState}
          isDataLoading={kpiLoading}
          drilldownKeys={drilldownKeys}
          handleDrildown={handleDrildown}
          removeDrilldown={removeDrilldown}
          autoValueModifiers={autoValueModifiers}
        />
      ),
    });
  };

  return (
    <FullScreen handle={fullScreenHandler}>
      <StyledOuterGridKpiLayout
        editPainel={!!editPainel}
        layoutType={gridSize && gridSize !== 'auto' ? 'fixed' : editPainel}
        showToolbar={showToolbar}
        isFullScreen={fullScreenHandler.active}
      >
        <div ref={gridKpiRef}>
          <StyledResponsiveReactGridKpiLayout
            fixedSize={gridSize}
            key={(!!editPainel).toString() + gridKpiState?.collection}
            editPainel={!!editPainel}
            isResizable={!!editPainel}
            isDraggable={!!editPainel}
            multiSelection={multiSelection}
            margin={getSpaceBetween(dashboardDoc?.cardStyles)}
            breakpoints={breakpoints}
            cols={rglCols}
            rowHeight={20}
            // layouts={{
            //   lg: kpisLayout.lg,
            //   sm: kpisLayout.sm,
            //   xxs: kpisLayout.xxs,
            // }}
            // onLayoutChange={(layout, _layouts) => {
            //   setStateKpisLayout(_layouts);
            // }}
            layouts={layoutsWithReportHeights}
            onLayoutChange={(layout, _layouts) => {
              setStateKpisLayout(_layouts);
            }}
            onBreakpointChange={holdBreakpoint}
            breakpoint={setBreakPoints(gridSize && gridSize !== 'auto' ? 'desktop' : editPainel)}
            compactType="vertical" // TODO: criar input para 3 opcoes: vertical, horizontal ou null
            preventCollision={false}
            useCSSTransforms={false}
            measureBeforeMount={false}
            resizeHandles={['s', 'e', 'se']}
          >
            {kpiItemList.map((item) => {
              // const kpiFile = files.find((f) => (f.file_id === item.database)) || {};
              const selectedGlobalFilters = globalFilters.current[item.database] || {};
              const definition = reportDefinitions[item.id];
              // const canEditReport = isOwner || isSuper;
              return (
                <CardWrapper
                  key={item.id}
                  style={getCardStyle(dashboardDoc?.cardStyles || {})}
                  isSelected={selectedCards && selectedCards.has(item.id)}
                >
                  {item?.type === 'ReportGrid' ? (
                    <ReportGridCard
                      item={item}
                      definition={definition}
                      canEditReport={isOwner}
                      collection={gridKpiState?.collection}
                      onReportRetry={onReportRetry}
                      cardSizes={
                        stateKpisLayout?.[gridBreakpoint]?.find((bk) => bk.i === item.id)
                      }
                      reportGridSettings={handleReportGridSettings}
                      onReportDensityChange={onReportDensityChange}
                      reportGridDetails={handleReportGridDetails}
                      editPainel={editPainel || multiSelection}
                      cardSelection={multiSelection ? toggleSelected : undefined}
                      updateReportHeight={updateReportHeight}
                      downloadImgCard={gridKpiAPI.downloadImgCard}
                      cloneCard={cloneCard}
                      transferKpi={transferKpi}
                      deleteCard={deleteCard}
                      dependencyFile={checkAccessToDependency(item)}
                      getKpiWithoutDependencyMessage={getKpiWithoutDependencyMessage}
                      createKpiComment={addComment}
                    />
                  ) : (
                    <GridKpisCard
                      isOwner={isOwner}
                      isSuper={isSuper}
                      item={item}
                      gridKpiState={gridKpiState}
                      editCard={editCard}
                      cloneCard={cloneCard}
                      transferKpi={transferKpi}
                      deleteCard={deleteCard}
                      openCardInfo={openCardInfo}
                      donwloadReport={downloadReport}
                      downloadImgCard={gridKpiAPI.downloadImgCard}
                      createKpiComment={addComment}
                      openModifiersModal={openModifiersModal}
                      autoValueModifiers={submitValueModifiers}
                      modifiersQtd={(
                        item?.meta?.filterValues?.length || 0
                      ) + (
                        item?.meta?.groupValues?.length || 0
                      )}
                      openKpiViewer={openKpiViewer}
                      viewKpiIdState={[viewKpiId, setViewKpiId]}
                      showToPrint={showToPrint}
                      getControlValues={getControlValues}
                      handleDynamicKpi={handleDynamicKpi}
                      cardSizes={
                        stateKpisLayout?.[gridBreakpoint]?.find((bk) => bk.i === item.id)
                      }
                      globalFilters={selectedGlobalFilters}
                      globalFiltersOrder={globalFiltersOrder}
                      dependencyFile={checkAccessToDependency(item)}
                      canAccessReport={checkUserPermission(item)}
                      getKpiWithoutDependencyMessage={getKpiWithoutDependencyMessage}
                      globalConfig={dashboardDoc?.cardStyles}
                      updateKpiShowOnExpand={updateKpiShowOnExpand}
                      editPainel={editPainel || multiSelection}
                      cardSelection={multiSelection ? toggleSelected : undefined}
                    />
                  )}
                </CardWrapper>
              );
            })}
          </StyledResponsiveReactGridKpiLayout>
        </div>
      </StyledOuterGridKpiLayout>
      { fullScreenHandler.active && (
        <ExitFullScreen>
          <Button color="secondary" variant="outlined" size="large" onClick={fullScreenHandler.exit}>
            Aperte ESC ou clique para sair
          </Button>
        </ExitFullScreen>
      )}
    </FullScreen>
  );
});

export default GridKpis;

GridKpis.propTypes = {
  gridKpiRef: PropTypes.shape({}),
  kpiItemList: PropTypes.arrayOf(PropTypes.shape({})),
  dashboardKey: PropTypes.string.isRequired,
  gridKpiAPI: PropTypes.shape({
    cloneCard: PropTypes.func,
    transferKpi: PropTypes.func,
    removeCard: PropTypes.func,
    downloadImgCard: PropTypes.func,
    downloadReportFromCard: PropTypes.func,
    saveKpiComment: PropTypes.func,
    saveKpiValueFilters: PropTypes.func,
    loadCardsFromCollection: PropTypes.func,
    updateKpiShowOnExpand: PropTypes.func,
  }),
  gridKpiState: PropTypes.shape({
    collection: PropTypes.string,
  }),
  dashboards: PropTypes.arrayOf(PropTypes.shape({})),
  dashboardDoc: PropTypes.shape({
    cardStyles: PropTypes.shape({}),
  }),
  kpisLayout: PropTypes.shape({
    sm: PropTypes.arrayOf(PropTypes.shape({})),
    xxs: PropTypes.arrayOf(PropTypes.shape({})),
    lg: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  saveLayout: PropTypes.func,
  isOwner: PropTypes.bool,
  isSuper: PropTypes.bool,
  isGuest: PropTypes.bool,
  editPainel: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  multiSelection: PropTypes.bool,
  selectedCards: PropTypes.instanceOf(Map),
  toggleSelected: PropTypes.func,
  gridSize: PropTypes.string,
  gridBreakpoint: PropTypes.string,
  setGridBreakpoint: PropTypes.func,
  showToolbar: PropTypes.bool,
  showToPrint: PropTypes.bool,
  getControlValues: PropTypes.func,
  handleDynamicKpi: PropTypes.func,
  fullScreenHandler: PropTypes.shape({
    active: PropTypes.bool,
    exit: PropTypes.func,
  }).isRequired,
  globalFilters: PropTypes.shape({
    current: PropTypes.objectOf(PropTypes.shape({})),
  }).isRequired,
  globalFiltersOrder: PropTypes.shape({
    current: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  dashboardFileDependencies: PropTypes.arrayOf(
    PropTypes.shape({}),
  ).isRequired,
  checkAccessToDependency: PropTypes.func,
  checkUserPermission: PropTypes.func,
  getKpiWithoutDependencyMessage: PropTypes.func,
  getUserName: PropTypes.func,
  handleIframe: PropTypes.func,
  handleSubsetTable: PropTypes.func,
  handleValueModifiers: PropTypes.func,
  subsetTableReachedLimit: PropTypes.func,
  subsetTableLimit: PropTypes.number,
  plan: PropTypes.string,
  reportDefinitions: PropTypes.objectOf(PropTypes.shape({})),
  onReportRetry: PropTypes.func,
  onReportDensityChange: PropTypes.func,
  handleReportGridSettings: PropTypes.func,
  handleReportGridDetails: PropTypes.func,
};
